import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, Circle, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function NewExperiencesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: result } = await api.get("/experiences/inbox", {
        params: { unread_only: unreadOnly },
      });
      setData(result);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const openExperience = async (item) => {
    if (!item.admin_read) {
      await api.patch(`/experiences/${item.id}/read`);
    }
    navigate(`/danisanlar/${item.client_id}?tab=deneyimler&file=${item.file_id || ""}&experience=${item.id}`);
  };

  return (
    <div className="p-3 sm:p-5 lg:p-6 max-w-[1100px]">
      <div className="flex items-start justify-between gap-3 sm:gap-5 mb-3 sm:mb-5">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            <h1 className="text-[20px] font-semibold tracking-tight">Yeni Deneyimler</h1>
          </div>
          <p className="text-[12px] text-pleiades-slate mt-1.5">
            Danışanların deneyim defterine yazdığı son kayıtları buradan takip edin.
          </p>
        </div>
        <button onClick={load} className="h-9 px-3 border border-pleiades-border bg-white text-[12px] flex items-center gap-2 hover:bg-pleiades-sidebar">
          <RefreshCw className="w-3.5 h-3.5" /> Yenile
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setUnreadOnly(true)}
          className={`h-8 px-3 text-[12px] border ${unreadOnly ? "bg-pleiades-ink text-white border-pleiades-ink" : "bg-white border-pleiades-border"}`}>
          Okunmamış ({data.unread_count})
        </button>
        <button onClick={() => setUnreadOnly(false)}
          className={`h-8 px-3 text-[12px] border ${!unreadOnly ? "bg-pleiades-ink text-white border-pleiades-ink" : "bg-white border-pleiades-border"}`}>
          Tümü
        </button>
      </div>

      {error && <div className="mb-4 bg-red-50 text-red-700 border border-red-200 px-4 py-3 text-[12px]">{error}</div>}
      {loading ? (
        <div className="text-[12px] text-pleiades-muted py-12 text-center">Deneyimler yükleniyor…</div>
      ) : data.items.length === 0 ? (
        <div className="bg-white border border-dashed border-pleiades-border py-16 text-center">
          <BookOpen className="w-7 h-7 mx-auto text-pleiades-muted mb-3" />
          <div className="text-[13px] font-medium">{unreadOnly ? "Yeni deneyim bulunmuyor" : "Henüz deneyim yazılmamış"}</div>
          <div className="text-[11px] text-pleiades-muted mt-1">Yeni bir kayıt geldiğinde burada görünecek.</div>
        </div>
      ) : (
        <div className="bg-white border border-pleiades-border">
          {data.items.map((item) => (
            <button key={item.id} onClick={() => openExperience(item)}
              className="w-full flex items-start gap-3 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 text-left border-b border-pleiades-border last:border-b-0 hover:bg-pleiades-sidebar/60">
              <div className="pt-1">
                <Circle className={`w-2.5 h-2.5 ${item.admin_read ? "text-pleiades-border" : "fill-pleiades-blue text-pleiades-blue"}`} />
              </div>
              <div className="w-44 shrink-0">
                <div className="text-[13px] font-semibold truncate">{item.client_name}</div>
                <div className="text-[11px] text-pleiades-muted truncate mt-0.5">{item.client_email}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium truncate">{item.title}</div>
                {item.file_name && <div className="text-[10px] text-pleiades-blue mt-0.5">PDF: {item.file_name}</div>}
                <div className="text-[12px] text-pleiades-slate line-clamp-2 mt-1 leading-relaxed">{item.preview}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[10px] text-pleiades-muted">{new Date(item.created_at).toLocaleString("tr-TR")}</div>
                <ChevronRight className="w-4 h-4 text-pleiades-muted ml-auto mt-3" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
